import { useCreateInvite, useGroupInvites, useRevokeInvite } from '@hobbytools/data';
import { friendlyError } from '@hobbytools/platform';
import { Banner, Button, Card } from '@hobbytools/ui';
import { useState } from 'react';

/** Invite codes for a group. Only rendered for admins and the owner (RLS enforces the same). */
export function Invites({ groupId, userId }: { groupId: string; userId: string }) {
  const invites = useGroupInvites(groupId, true);
  const create = useCreateInvite(groupId);
  const revoke = useRevokeInvite(groupId);
  const [copied, setCopied] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const active = (invites.data ?? []).filter((i) => !i.revoked_on);
  const linkFor = (code: string) => `${window.location.origin}/join/${code}`;

  const copy = async (code: string) => {
    await navigator.clipboard.writeText(linkFor(code));
    setCopied(code);
  };

  return (
    <Card className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="heading-display text-xl">Invites</h2>
        <Button
          size="sm"
          isDisabled={create.isPending}
          onPress={async () => {
            setError(null);
            try {
              await create.mutateAsync({ createdBy: userId });
            } catch (err) {
              setError(friendlyError(err));
            }
          }}
        >
          New invite
        </Button>
      </div>
      {error ? <Banner tone="danger">{error}</Banner> : null}
      {active.length === 0 ? (
        <p className="text-sm text-fg-muted">
          No active invites. Create one and share the link with your group.
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-border">
          {active.map((i) => (
            <li key={i.id} className="flex flex-wrap items-center gap-3 py-2">
              <code className="font-mono text-lg" data-testid="invite-code">
                {i.code}
              </code>
              <span className="text-sm text-fg-muted">
                Used {i.uses}
                {i.max_uses ? ` of ${i.max_uses}` : ''} {i.uses === 1 && !i.max_uses ? 'time' : 'times'}
              </span>
              <span className="ml-auto flex gap-2">
                <Button size="sm" variant="secondary" onPress={() => copy(i.code)}>
                  {copied === i.code ? 'Link copied' : 'Copy link'}
                </Button>
                <Button size="sm" variant="ghost" onPress={() => revoke.mutate(i.id)}>
                  Revoke
                </Button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
